"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.getCompiledFormula = exports.compileFormula = exports.MAX_FORMULA_LENGTH = exports.FormulaCompileError = void 0;
exports.evaluateFormula = evaluateFormula;
const mathjs_1 = require("mathjs");
const lruCache_1 = require("./lruCache");
const mathScope_1 = require("./mathScope");
const sandbox_1 = require("./sandbox");
class FormulaCompileError extends Error {
    formula;
    constructor(message, formula, options) {
        super(message, options);
        this.formula = formula;
        this.name = 'FormulaCompileError';
    }
}
exports.FormulaCompileError = FormulaCompileError;
exports.MAX_FORMULA_LENGTH = 10_000;
const MAX_UNDERSCORE_RUN = 8;
const QUOTED_PLACEHOLDER = /^\{([^{}]*)\}$/;
const findStringEnd = (formula, start) => {
    const quote = formula[start];
    for (let i = start + 1; i < formula.length; i++) {
        if (formula[i] === '\\') {
            i++;
        }
        else if (formula[i] === quote) {
            return i;
        }
    }
    return -1;
};
const hasPlaceholder = (text) => {
    const open = text.indexOf('{');
    return open !== -1 && text.indexOf('}', open + 1) !== -1;
};
// Replaces each {token} with a generated symbol, so values never become formula text
const rewritePlaceholders = (formula) => {
    // One more underscore than the formula's longest run, so no written name can match a generated one
    const longestRun = Math.max(0, ...(formula.match(/_+/g) ?? []).map((run) => run.length));
    // Counts every run, placeholders and text included, so generated names stay short
    if (longestRun > MAX_UNDERSCORE_RUN) {
        throw new Error(`More than ${MAX_UNDERSCORE_RUN} underscores in a row are not supported`);
    }
    const prefix = `${'_'.repeat(longestRun + 1)}v`;
    const bindings = new Map();
    const symbolFor = (token, quoted) => {
        const key = `${quoted}:${token}`;
        let binding = bindings.get(key);
        if (!binding) {
            binding = { token, symbol: `${prefix}${bindings.size}`, quoted };
            bindings.set(key, binding);
        }
        // Spaces keep `{a}mod 2` from becoming one name
        return ` ${binding.symbol} `;
    };
    let expression = '';
    let i = 0;
    while (i < formula.length) {
        const char = formula[i];
        if (char === '#') {
            // mathjs would ignore it, but text substitution still replaced placeholders inside it
            throw new Error('Comments are not supported');
        }
        if (char === '"' || char === "'") {
            const end = findStringEnd(formula, i);
            if (end === -1) {
                // Unterminated text, let the parser report it
                expression += formula.slice(i);
                break;
            }
            const body = formula.slice(i + 1, end);
            const quoted = QUOTED_PLACEHOLDER.exec(body);
            if (quoted) {
                expression += symbolFor(quoted[1], true);
            }
            else if (hasPlaceholder(body)) {
                throw new Error(`Variable inside text is not supported: ${formula.slice(i, end + 1)}`);
            }
            else {
                expression += formula.slice(i, end + 1);
            }
            i = end + 1;
            continue;
        }
        if (char === '{') {
            const close = formula.indexOf('}', i + 1);
            if (close === -1) {
                expression += formula.slice(i);
                break;
            }
            expression += symbolFor(formula.slice(i + 1, close), false);
            i = close + 1;
            continue;
        }
        expression += char;
        i++;
    }
    return { expression, bindings: [...bindings.values()] };
};
const MATH_SCOPE_ENTRIES = Object.entries(mathScope_1.mathScope);
class Unreadable {
    message;
    constructor(message) {
        this.message = message;
    }
}
// Reading N/A, N/R or other text unquoted fails, as the undefined symbol did with text substitution
class FormulaScope extends Map {
    get(key) {
        const value = super.get(key);
        if (value instanceof Unreadable) {
            throw new Error(value.message);
        }
        return value;
    }
}
const toScopeValue = (value, { token, quoted }) => {
    if (quoted) {
        return String(value);
    }
    if (typeof value === 'number') {
        // Text substitution wrote -0 as "0"
        return value === 0 ? 0 : value;
    }
    if (value === mathScope_1.NOT_APPLICABLE || value === mathScope_1.NOT_REPORTED) {
        return new Unreadable(`Undefined symbol ${value}`);
    }
    // Text substitution turned "12" into the number 12. Number() also accepts Unicode spaces and 0X10,
    // rejects -0x10, and turns "  " into 0
    const number = value.trim() ? Number(value) : NaN;
    if (!Number.isNaN(number)) {
        return number;
    }
    return new Unreadable(`Variable {${token}} is not a number`);
};
// Own properties only; throws for values that aren't a number or text
const readVariable = (variables, token) => {
    if (!Object.hasOwn(variables, token)) {
        return undefined;
    }
    const value = variables[token];
    if (value === undefined || value === null || value === '') {
        return undefined;
    }
    if (typeof value !== 'number' && typeof value !== 'string') {
        throw new Error(`Variable {${token}} is not a number or text`);
    }
    return value;
};
const errorMessage = (e) => {
    try {
        return e instanceof Error && typeof e.message === 'string' ? e.message : String(e);
    }
    catch {
        // Thrown values without a text form, e.g. Object.create(null)
        return 'Unknown error';
    }
};
const toFailure = (e) => {
    const message = errorMessage(e);
    // number("{a}") fails with a different message than the api helpers know
    if ((0, mathScope_1.isNAError)({ message }) || message === `String "${mathScope_1.NOT_APPLICABLE}" is not a valid number`) {
        return { ok: false, reason: 'not-applicable', message };
    }
    if ((0, mathScope_1.isNRError)({ message }) || message === `String "${mathScope_1.NOT_REPORTED}" is not a valid number`) {
        return { ok: false, reason: 'not-reported', message };
    }
    return { ok: false, reason: 'error', message };
};
const checkResult = (result, expected) => {
    if (typeof result === 'number' && expected === 'number') {
        return Number.isFinite(result) ? { ok: true, value: result } : { ok: false, reason: 'not-finite' };
    }
    if (typeof result === 'string' && expected === 'string') {
        return { ok: true, value: result };
    }
    // e.g. sqrt of a negative answer: no real result for these values, like a divide by zero
    if ((0, mathjs_1.isComplex)(result) && expected === 'number') {
        return { ok: false, reason: 'not-finite' };
    }
    return { ok: false, reason: 'invalid-type', value: result };
};
// mathjs errors quote the rewritten formula: put each {token} back, and drop the position, which can't be mapped
const toAuthorMessage = (message, bindings) => bindings
    .reduce((text, { symbol, token, quoted }) => text.replace(new RegExp(` ?${symbol}(?!\\d) ?`, 'g'), () => (quoted ? `"{${token}}"` : `{${token}}`)), message)
    .replace(/\s*\(char \d+\)$/, '');
/** Parses and checks a formula once; throws FormulaCompileError when it can't be used */
const compileFormula = (formula) => {
    // Callers being migrated are untyped
    if (typeof formula !== 'string') {
        throw new FormulaCompileError('Formula must be text', '');
    }
    if (!formula.trim()) {
        throw new FormulaCompileError('Formula is empty', formula);
    }
    if (formula.length > exports.MAX_FORMULA_LENGTH) {
        throw new FormulaCompileError(`Formula is longer than ${exports.MAX_FORMULA_LENGTH} characters`, formula);
    }
    let bindings = [];
    let code;
    try {
        // A leading or trailing newline would make mathjs parse a block of statements
        const rewritten = rewritePlaceholders(formula.trim());
        bindings = rewritten.bindings;
        const node = (0, sandbox_1.parse)(rewritten.expression);
        (0, sandbox_1.checkNode)(node, new Map(bindings.map((b) => [b.symbol, b.token])));
        code = node.compile();
    }
    catch (e) {
        // Includes stack overflows from deeply nested formulas; keeps the caller's untrimmed text
        throw new FormulaCompileError(toAuthorMessage(errorMessage(e), bindings), formula, { cause: e });
    }
    const placeholders = Object.freeze([...new Set(bindings.map((b) => b.token))]);
    const quotedPlaceholders = Object.freeze([...new Set(bindings.filter((b) => b.quoted).map((b) => b.token))]);
    const evaluate = (variables, options) => {
        try {
            // Callers being migrated are untyped
            const { missing = 'zero', expected = 'number' } = options ?? {};
            const values = variables ?? {};
            const read = new Map(placeholders.map((token) => [token, readVariable(values, token)]));
            const absent = placeholders.filter((token) => read.get(token) === undefined);
            if (placeholders.length > 0 && absent.length === placeholders.length) {
                return { ok: false, reason: 'missing' };
            }
            if (missing === 'missing' && absent.length > 0) {
                return { ok: false, reason: 'missing' };
            }
            const scope = new FormulaScope(MATH_SCOPE_ENTRIES);
            for (const binding of bindings) {
                scope.set(binding.symbol, toScopeValue(read.get(binding.token) ?? 0, binding));
            }
            return checkResult(code.evaluate(scope), expected);
        }
        catch (e) {
            return toFailure(e);
        }
    };
    return { formula, placeholders, quotedPlaceholders, evaluate: evaluate };
};
exports.compileFormula = compileFormula;
const cache = new lruCache_1.LruCache(1000);
const tryCompile = (formula) => {
    try {
        return (0, exports.compileFormula)(formula);
    }
    catch (e) {
        return e instanceof FormulaCompileError ? e : new FormulaCompileError(errorMessage(e), formula, { cause: e });
    }
};
/** Compiles with the shared cache; returns the error instead of throwing */
const getCompiledFormula = (formula) => {
    // Objects from untyped callers and over-long text mustn't be cache keys
    if (typeof formula !== 'string' || formula.length > exports.MAX_FORMULA_LENGTH) {
        return tryCompile(formula);
    }
    const cached = cache.get(formula);
    if (cached) {
        return cached;
    }
    const compiled = tryCompile(formula);
    // A RangeError (stack overflow) depends on the caller's stack, so it may compile next time
    if (!(compiled instanceof FormulaCompileError && compiled.cause instanceof RangeError)) {
        cache.set(formula, compiled);
    }
    return compiled;
};
exports.getCompiledFormula = getCompiledFormula;
function evaluateFormula(formula, variables, options) {
    const compiled = (0, exports.getCompiledFormula)(formula);
    if (compiled instanceof FormulaCompileError) {
        return { ok: false, reason: 'invalid-formula', message: compiled.message };
    }
    return compiled.evaluate(variables, options);
}
